import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NavbarAr from "@/components/NavbarAr";
import FooterAr from "@/components/FooterAr";
import EventName from "@/components/EventName";
import Brand from "@/components/Brand";
import BrandText from "@/components/BrandText";

// Gallery — restrained interim institutional composition. Home of the
// CrewZNatioN publications. Publications are named directly by title;
// no internal production terminology surfaces to the visitor.
//
// Bilingual (Feb 2026): the Arabic locale swaps every editorial string
// to its owner-approved Arabic equivalent while preserving the exact
// English structure, imagery, colours, typography, and spacing.

export default function Gallery({ locale = "en", embedded = false }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const PageFooter = isAr ? FooterAr : Footer;
    return (
        <div dir={isAr ? "rtl" : "ltr"} id={embedded ? "gallery" : undefined} className={`${embedded ? "" : "min-h-screen "}bg-[var(--crewz-bg)] text-[var(--crewz-text)]`} data-testid="gallery-page">
            {!embedded && <Nav />}

            <section className="px-6 md:px-12 lg:px-20 pt-24 pb-32 max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-5">
                    <span className="gold-rule" />
                    <p className="overline overline-primary">{isAr ? "المعرض" : "The Gallery"}</p>
                </div>
                <h1
                    className={`font-display text-5xl md:text-6xl text-[var(--crewz-text)] leading-[0.98] max-w-3xl${isAr ? " !leading-[60px] md:!leading-[72px]" : ""}`}
                    data-testid="gallery-title"
                >
                    {isAr ? (
                        <>المعرض يبدأ مع <BrandText>The Farm</BrandText></>
                    ) : (
                        <>The Gallery arrives with The Farm.</>
                    )}
                </h1>

                <div className="mt-10 max-w-2xl space-y-6 font-body text-base md:text-lg text-[var(--crewz-text)]/85 font-light leading-relaxed">
                    {isAr ? (
                        <>
                            <p>
                                يجري إعداد المعرض ضمن مشروع <BrandText>The Farm</BrandText>، ليكون السجل البصري الذي يوثق رحلة <Brand /> وتطورها كمؤسسة. وسيُفتح للجمهور عند اكتمال جاهزيته للنشر.
                            </p>
                            <p>
                                وحتى افتتاح المعرض، تظل صور ومقاطع فيديو النسخة الأولى من <EventName /> أحدث سجلٍ بصري يوثق رحلة المجتمع ويجسد ذاكرته البصرية.
                            </p>
                        </>
                    ) : (
                        <>
                            <p>
                                The Gallery is being composed as part of The Farm — <Brand />&apos;s visual record of the institution. It will open when the publication is ready.
                            </p>
                            <p>
                                Until then, the photographs and films from the first edition of <EventName /> carry the community&apos;s most recent visual record.
                            </p>
                        </>
                    )}
                </div>

                <div
                    className="mt-16 md:mt-20 max-w-3xl"
                    data-testid="gallery-publications"
                >
                    <ol className="space-y-6">
                        {[
                            { title: "The Farm", testid: "publication-the-farm", slug: "the-farm" },
                            { title: "CrewZNatioN X RShield Cars & Coffee", testid: "publication-rshield-cars-coffee", slug: "crewznation-x-rshield-cars-and-coffee" },
                            { title: "The Desert End", testid: "publication-the-desert-end", slug: "the-desert-end" },
                        ].map((item) => (
                            <li
                                key={item.testid}
                                data-testid={item.testid}
                                className="font-display text-2xl md:text-3xl text-[var(--crewz-text)] leading-tight"
                            >
                                <Link
                                    to={`${isAr ? "/ar/events" : "/events"}#${item.slug}`}
                                    className="text-inherit no-underline hover:text-inherit focus:text-inherit"
                                    data-testid={`${item.testid}-link`}
                                >
                                    <BrandText>{item.title}</BrandText>
                                </Link>
                            </li>
                        ))}
                    </ol>
                    <p className="font-body text-sm text-[var(--crewz-text)]/60 font-light leading-relaxed mt-8 max-w-2xl">
                        {isAr
                            ? "سيعرض المعرض كل إصدارٍ كاملًا فور نشره."
                            : "The Gallery will present each publication in full upon release."}
                    </p>
                </div>

                <div className="mt-14">
                    <Link
                        to={isAr ? "/ar/gathering" : "/gathering"}
                        className="inline-flex items-center gap-2 font-body text-sm tracking-[0.22em] uppercase font-bold text-[var(--crewz-gold)] hover:opacity-80 transition-opacity"
                        data-testid="gallery-gathering-link"
                    >
                        {isAr ? (
                            <>من داخل <EventName /> {"\u2190"}</>
                        ) : (
                            <>Inside <EventName /> &rarr;</>
                        )}
                    </Link>
                </div>
            </section>

            {!embedded && <PageFooter />}
        </div>
    );
}
